/**
 * Match Result Modal Component
 * Uses PadelMatchResultForm for adding match results
 */

"use client";

import { submitMatchResult } from "@/actions/match";
import MatchResultForm, {
  defaultMatchResultValue,
  MatchResultFormValue,
} from "@/components/forms/MatchResultForm";
import ResultConfirmation from "@/components/forms/ResultConfirmation";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedbackPrompt } from "@/contexts/FeedbackPromptContext";
import { logMatchEvent } from "@/lib/analytics";
import { detectWinner } from "@/lib/sports";
import {
  Match,
  PadelMatchResult,
  PickleballMatchResult,
  Sport,
  SportMatchResult,
} from "@/types";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useTranslations } from "next-intl";
import { useState } from "react";

/**
 * Build a SportMatchResult from the form value, mirroring the mapping the
 * server action uses (actions/match.ts) so the confirmation preview's detected
 * winner matches what will actually be recorded.
 */
function buildResultPreview(
  value: MatchResultFormValue,
  matchId: string,
): SportMatchResult {
  const base = {
    id: matchId,
    matchId,
    tournamentId: "",
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  if (value.sport === "padel") {
    const sets = value.sets;
    const result: PadelMatchResult = {
      ...base,
      sport: "padel",
      set1: sets[0],
      set2: sets[1],
      set3: sets[2] || { team1Games: 0, team2Games: 0, tieBreak: undefined },
    };
    return result;
  }

  const result: PickleballMatchResult = {
    ...base,
    sport: "pickleball",
    games: value.games.filter((g) => g.team1Points > 0 || g.team2Points > 0),
  };
  return result;
}

interface MatchResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  matchId?: string;
  sport: Sport["slug"];
  team1Name?: string;
  team2Name?: string;
  team1Id?: string;
  team2Id?: string;
  submittingTeamId?: string;
}

export function MatchResultModal({
  isOpen,
  onClose,
  onSuccess,
  matchId,
  sport,
  team1Name,
  team2Name,
  team1Id,
  team2Id,
  submittingTeamId,
}: MatchResultModalProps) {
  const t = useTranslations();
  const { user } = useAuth();
  const { trackAction } = useFeedbackPrompt();
  const [result, setResult] = useState<MatchResultFormValue>(() =>
    defaultMatchResultValue(sport),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [preview, setPreview] = useState<{
    result: SportMatchResult;
    winnerId: string;
  } | null>(null);

  // Build the result, detect the winner, and move to the confirmation step.
  // Stays on the form if no winner can be determined (the server would reject
  // it too) — surfaced early for a clearer UX.
  const handleReview = () => {
    setError(null);

    if (!matchId || !submittingTeamId || !user?.id) {
      setError(t("matches.failedToSubmitResult"));
      return;
    }

    const built = buildResultPreview(result, matchId);
    const winnerId = detectWinner(
      { id: matchId, team1Id, team2Id, sport } as Match,
      built,
    );

    if (!winnerId) {
      setError(t("errors.couldNotDetermineWinner"));
      return;
    }

    setPreview({ result: built, winnerId });
    setStep("confirm");
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    if (!matchId || !submittingTeamId || !user?.id) {
      setError(t("matches.failedToSubmitResult"));
      setIsSubmitting(false);
      return;
    }

    try {
      const payload =
        result.sport === "padel"
          ? {
              matchId,
              sport: "padel" as const,
              sets: result.sets,
              submittingTeamId,
              submittingUserId: user.id,
            }
          : {
              matchId,
              sport: "pickleball" as const,
              games: result.games,
              submittingTeamId,
              submittingUserId: user.id,
            };
      const response = await submitMatchResult(payload);

      if (response.success) {
        // Show success toast
        addToast({
          title: t("matches.resultSubmitted"),
          description: t("matches.resultSubmittedSuccess"),
          color: "success",
        });
        logMatchEvent("complete", matchId);
        trackAction(); // Track for feedback prompt
        setStep("form");
        setPreview(null);
        onSuccess?.();
        onClose();
      } else {
        // Result rejected server-side — return to the form to fix it.
        setStep("form");
        setError(response.error || t("matches.failedToSubmitResult"));
      }
    } catch (err) {
      console.error("Failed to submit match result:", err);
      setError(t("matches.failedToSubmitResult"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setStep("form");
      setPreview(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      placement="center"
      onClose={handleClose}
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
      hideCloseButton
      backdrop="opaque"
      size="lg"
      className="medieval-modal"
      classNames={{
        wrapper: "z-[200] max-sm:!items-start max-sm:!p-0",
        backdrop: "bg-black/80",
        base: "bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl max-sm:!m-0 max-sm:!rounded-none max-sm:!h-[100dvh] max-sm:!max-h-[100dvh] max-sm:!w-full max-sm:!max-w-full max-sm:!border-0",
        header: "border-b border-[var(--border-subtle)]",
        body: "py-6 max-sm:!flex-1 max-sm:!overflow-y-auto",
        footer: "border-t border-[var(--border-subtle)] max-sm:!sticky max-sm:!bottom-0 max-sm:bg-[var(--bg-card)]",
      }}
      motionProps={{
        onAnimationComplete: () => {
          // Ensure focus is properly managed when modal opens
          if (isOpen) {
            const firstFocusable = document.querySelector(
              '[role="dialog"] input, [role="dialog"] button:not([disabled])',
            ) as HTMLElement;
            firstFocusable?.focus();
          }
        },
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 text-primary font-cinzel">
          <h3 className="text-xl">
            {step === "confirm"
              ? `✅ ${t("matches.confirmResultTitle")}`
              : `📝 ${t("matches.addMatchResults")}`}
          </h3>
        </ModalHeader>
        <ModalBody>
          {step === "confirm" && preview ? (
            <ResultConfirmation
              sport={sport}
              result={preview.result}
              winnerId={preview.winnerId}
              team1Id={team1Id}
              team2Id={team2Id}
              team1Name={team1Name}
              team2Name={team2Name}
            />
          ) : (
            <MatchResultForm
              sport={sport}
              value={result}
              onChange={setResult}
              disabled={isSubmitting}
              team1Id={team1Id}
              team2Id={team2Id}
              team1Name={team1Name}
              team2Name={team2Name}
            />
          )}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
              <p className="text-sm text-danger-600 dark:text-danger-400">
                {error}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {step === "confirm" ? (
            <>
              <Button
                variant="light"
                onPress={() => setStep("form")}
                isDisabled={isSubmitting}
                className="font-semibold"
              >
                {t("common.back")}
              </Button>
              <Button
                onPress={handleSubmit}
                isLoading={isSubmitting}
                className="bg-gradient-to-r from-[var(--gold-dark)] to-[var(--gold)] text-black font-semibold hover:shadow-lg hover:shadow-[var(--gold)]/50 transition-shadow"
              >
                {t("matches.confirmAndSubmit")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="light"
                onPress={handleClose}
                isDisabled={isSubmitting}
                className="font-semibold"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onPress={handleReview}
                className="bg-gradient-to-r from-[var(--gold-dark)] to-[var(--gold)] text-black font-semibold hover:shadow-lg hover:shadow-[var(--gold)]/50 transition-shadow"
              >
                {t("common.submit")}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
